// Build a self-contained, mobile-optimized HTML landing page from the
// backend's LandingPageResponse. All styling is inlined (no external CSS
// or JS beyond the Tailwind Play CDN) so users can paste it anywhere.

const escapeHtml = (s = "") =>
    String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export const buildLandingHtml = (data) => {
    const c = data?.content || {};
    const businessType = data?.business_type || "your service";
    const city = data?.city || "";
    const imgSrc =
        data?.image_b64
            ? `data:${data.image_mime || "image/png"};base64,${data.image_b64}`
            : "";

    const title = `${businessType} in ${city}`.trim();

    const uspList = (c.usp_bullets || [])
        .map(
            (b) => `
      <li class="flex items-start gap-3">
        <span class="mt-1 inline-block h-2 w-2 flex-none rounded-full bg-blue-600"></span>
        <span class="text-gray-800">${escapeHtml(b)}</span>
      </li>`
        )
        .join("");

    const featureCards = (c.features || [])
        .map(
            (f) => `
      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 class="text-lg font-semibold text-gray-900">${escapeHtml(f.title)}</h3>
        <p class="mt-2 text-sm leading-relaxed text-gray-600">${escapeHtml(f.description)}</p>
      </div>`
        )
        .join("");

    const sectionBlocks = (c.sections || [])
        .map(
            (s, i) => `
      <section class="${i % 2 === 0 ? "bg-white" : "bg-gray-50"} px-5 py-12 sm:px-8">
        <div class="mx-auto max-w-3xl">
          <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">${escapeHtml(s.title)}</h2>
          <p class="mt-4 text-base leading-7 text-gray-700">${escapeHtml(s.body)}</p>
        </div>
      </section>`
        )
        .join("");

    const faqItems = (c.faq || [])
        .map(
            (q) => `
      <details class="group border-b border-gray-200 py-4">
        <summary class="flex cursor-pointer items-center justify-between text-base font-medium text-gray-900">
          <span>${escapeHtml(q.title)}</span>
          <span class="ml-4 text-gray-400 group-open:rotate-45 transition">+</span>
        </summary>
        <p class="mt-2 pr-6 text-sm leading-6 text-gray-600">${escapeHtml(q.body)}</p>
      </details>`
        )
        .join("");

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)}</title>
<meta name="description" content="${escapeHtml(c.hero_subheadline || "")}" />
<script src="https://cdn.tailwindcss.com"></script>
<style>
  html, body { -webkit-font-smoothing: antialiased; }
  body { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
</style>
</head>
<body class="bg-white text-gray-900">

<!-- Hero -->
<header class="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white">
  <div class="mx-auto max-w-6xl px-5 pb-10 pt-8 sm:px-8 sm:pt-14 lg:grid lg:grid-cols-2 lg:gap-12 lg:pb-16">
    <div class="flex flex-col justify-center">
      ${c.hero_eyebrow
            ? `<p class="inline-block w-max rounded-full bg-blue-600/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-700">${escapeHtml(c.hero_eyebrow)}</p>`
            : ""}
      <h1 class="mt-4 text-4xl font-black leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
        ${escapeHtml(c.hero_headline)}
      </h1>
      <p class="mt-5 text-lg leading-8 text-gray-700 sm:text-xl">
        ${escapeHtml(c.hero_subheadline)}
      </p>
      <div class="mt-8 flex flex-wrap gap-3">
        ${c.hero_cta_primary
            ? `<a href="#contact" class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-md transition hover:bg-blue-700">${escapeHtml(c.hero_cta_primary)}</a>`
            : ""}
        ${c.hero_cta_secondary
            ? `<a href="tel:+000000000" class="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-900 transition hover:border-gray-400">${escapeHtml(c.hero_cta_secondary)}</a>`
            : ""}
      </div>
      ${c.trust_line
            ? `<p class="mt-6 text-sm font-medium text-gray-600">${escapeHtml(c.trust_line)}</p>`
            : ""}
    </div>
    <div class="mt-10 lg:mt-0">
      ${imgSrc
            ? `<img src="${imgSrc}" alt="${escapeHtml(businessType)} in ${escapeHtml(city)}" class="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl ring-1 ring-black/5" />`
            : `<div class="aspect-[4/3] w-full rounded-3xl bg-gray-100 ring-1 ring-black/5"></div>`}
    </div>
  </div>
</header>

<!-- USPs -->
${uspList
    ? `<section class="mx-auto max-w-3xl px-5 py-10 sm:px-8">
  <ul class="grid gap-4 sm:grid-cols-2">${uspList}</ul>
</section>`
    : ""}

<!-- Features -->
${featureCards
    ? `<section class="mx-auto max-w-6xl px-5 pb-12 sm:px-8">
  <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Why ${escapeHtml(city)} chooses us</h2>
  <div class="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">${featureCards}</div>
</section>`
    : ""}

<!-- Sections -->
${sectionBlocks}

<!-- Testimonial -->
${c.testimonial_quote
    ? `<section class="bg-blue-600 px-5 py-14 text-white sm:px-8">
  <div class="mx-auto max-w-3xl text-center">
    <p class="text-2xl font-medium leading-9 sm:text-3xl">&ldquo;${escapeHtml(c.testimonial_quote)}&rdquo;</p>
    <p class="mt-4 text-sm font-semibold uppercase tracking-widest text-blue-100">— ${escapeHtml(c.testimonial_author || "Verified customer")}</p>
  </div>
</section>`
    : ""}

<!-- FAQ -->
${faqItems
    ? `<section class="mx-auto max-w-3xl px-5 py-14 sm:px-8">
  <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Frequently asked</h2>
  <div class="mt-6">${faqItems}</div>
</section>`
    : ""}

<!-- Footer CTA -->
<section id="contact" class="bg-gray-900 px-5 py-16 text-center text-white sm:px-8">
  <div class="mx-auto max-w-2xl">
    <h2 class="text-3xl font-black tracking-tight sm:text-4xl">${escapeHtml(c.footer_cta_headline || "Ready to get started?")}</h2>
    <div class="mt-8">
      <a href="tel:+000000000" class="inline-flex items-center justify-center rounded-lg bg-white px-8 py-4 text-base font-semibold text-gray-900 shadow-lg transition hover:bg-gray-100">
        ${escapeHtml(c.footer_cta_button || c.hero_cta_primary || "Contact us")}
      </a>
    </div>
    <p class="mt-6 text-sm text-gray-400">Serving ${escapeHtml(city)} and surrounding areas</p>
  </div>
</section>

<footer class="border-t border-gray-800 bg-gray-900 px-5 py-6 text-center text-xs text-gray-500 sm:px-8">
  © ${new Date().getFullYear()} ${escapeHtml(businessType)} · ${escapeHtml(city)}
</footer>

</body>
</html>
`;
};
